import { useState } from "react";
import { useSession } from "@otto/plattform";
import { useClassCourses } from "../../courses/lib/data.js";
import { useResults } from "../../results/lib/data.js";
import { summarize } from "../lib/summary.js";
import { useSchool } from "../../school/lib/data.js";
import { useCertificateDownload } from "../hooks/useCertificateDownload.js";
import styles from "../../../shared/components/shell.module.css";

/**
 * Die Teilnahmeurkunde für die Klasse.
 *
 * Die Urkunde nennt einen Lehrgang, also wird er gewählt – aber nur, wenn die
 * Klasse mehr als einen führt. Bei genau einem gäbe es nichts zu wählen, und ein
 * Menü mit einem Eintrag ist eine Frage ohne Antwortmöglichkeit.
 *
 * Ob der Lehrgang zu Ende geübt ist, prüft hier nichts: die Lehrkraft druckt am
 * letzten Schultag, nicht wenn die Datenbank es erlaubt.
 */
export function CertificateButton({
  classId,
  className,
  schoolYear,
}: {
  classId: string;
  className: string;
  schoolYear: string;
}) {
  const { client, state } = useSession();
  const schoolId =
    state.kind === "signedIn" ? (state.identity.profile?.schoolId ?? "") : "";
  const courses = useClassCourses(client, classId);
  const school = useSchool(client, schoolId);
  const results = useResults(client, classId);
  const certificate = useCertificateDownload();
  const [chosen, setChosen] = useState<string | null>(null);

  const assigned = courses.data ?? [];
  if (assigned.length === 0) return null;

  const course = assigned.find((c) => c.curriculumId === chosen) ?? assigned[0]!;

  const gemeinsam = {
    schoolClass: className,
    schoolYear,
    schoolName: school.data?.name ?? "",
    courseTitle: course.title,
    courseSubtitle: course.subtitle,
  };

  const students = (results.data ?? []).map((row) => ({
    code: row.code,
    summary: summarize(row, course.bundle),
  }));

  return (
    <div className={styles.row} style={{ marginBottom: 18 }}>
      <button
        type="button"
        className={styles.button}
        disabled={certificate.printing}
        onClick={() => {
          void certificate.download(gemeinsam);
        }}
      >
        {certificate.printing ? "Wird erzeugt …" : "Urkunde als PDF"}
      </button>

      {students.length > 0 && (
        <button
          type="button"
          className={styles.buttonQuiet}
          disabled={certificate.printing}
          onClick={() => {
            void certificate.download({ ...gemeinsam, students });
          }}
        >
          {`Urkunden je Code (${students.length})`}
        </button>
      )}

      {assigned.length > 1 && (
        <label className={styles.subtitle} style={{ margin: 0 }}>
          für{" "}
          <select
            value={course.curriculumId}
            onChange={(event) => setChosen(event.target.value)}
          >
            {assigned.map((option) => (
              <option key={option.curriculumId} value={option.curriculumId}>
                {option.title}
              </option>
            ))}
          </select>
        </label>
      )}

      <span className={styles.subtitle} style={{ margin: 0 }}>
        Den Namen schreiben Sie von Hand darauf – auf beiden Formen.
      </span>
    </div>
  );
}
